import React, { FunctionComponent, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Row, Col, Card, Button } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import CircularProgress from '@material-ui/core/CircularProgress'
import Sidebar from '../../../components/ApplicantDashboard/Sidebar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import UploadProviderFileAuditModal from '../../../components/Modals/UploadFileAuditModal'
import RootState from '../../../types/RootState'
import { updateProviderFileAuditItem } from '../../../redux/actions/providerFileAudit'
import { getUploadFileAuditModalData } from '../../../utils/dataTools'

import './index.css'

interface Props {
  height: number;
  onReturnToAll: () => void;
}


const initialFiles = {
  w9Id: null,
  w9: [],
  w9File: null,
  activeCertificateLicensureId: null,
  activeCertificateLicensure: [],
  activeCertificateLicensureFile: null,
  activeLiabilityInsuranceId: null,
  activeLiabilityInsurance: [],
  activeLiabilityInsuranceFile: null,
  activeHomeownersInsuranceId: null,
  activeHomeownersInsurance: [],
  activeHomeownersInsuranceFile: null,
  activeAutoInsuranceId: null,
  activeAutoInsurance: [],
  activeAutoInsuranceFile: null,
  itemType: '',
}

const renderStatus = (cellContent, row) => cellContent === 'SUBMITTED' ? <FontAwesomeIcon icon={['fas', 'check-circle']} size='2x' color='#75b465' /> : <FontAwesomeIcon icon={['fas', 'circle']} size='2x' color='#cccccc' />

const renderActionButtons = (row: any, handleOpenModal: (itemType: string, readonly: boolean) => void) => {
  return (
    <Button 
      className='action-button'
      variant='info'
      onClick={() => handleOpenModal(row.itemType, row.status === 'SUBMITTED')}
    >
      {`${row.status === 'SUBMITTED' ? 'View' : 'Upload'}`}
    </Button>
  )
}

const checkFileAuditItems = (items: any) =>  items.length > 0 ? ( items.every(item => item.status === 'SUBMITTED') ? [] : items ) : []

const Audit: FunctionComponent<Props> = ({ height, onReturnToAll }: Props) => {
  const dispatch = useDispatch()

  const { audit, auditResult, loading, updateProviderFileAuditItemResult, selectedOrganization, selectedProvider, user } = useSelector((state: RootState) => ({
    audit: state.providerFileAudit.selectedProviderFileAudit,
    auditResult: state.providerFileAudit.audit,
    loading: state.providerFileAudit.audit.loading,
    updateProviderFileAuditItemResult: state.providerFileAudit.updateProviderFileAuditItem,
    selectedOrganization: state.authentication.selectedOrganization,
    selectedProvider: state.authentication.selectedProvider,
    user: state.authentication.user.data,
  }))

  const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''

  const data = loading === false ? (
    audit && audit.item && audit.item.every(itemElem => itemElem.status === 'SUBMITTED') ? 
      (<div><FontAwesomeIcon icon={['fas', 'check-circle']} size='2x' color='#75b465' /><span style={{ fontSize: 17, marginLeft: 5, verticalAlign: 'super' }}>No items missing</span></div>) : 
      (<span>No audits available</span>)
  ) : (
    <div className='organization-circular-progress'>
      <CircularProgress size={40} />
      <span>Loading data</span>
    </div>
  )

  // Upload Modal
  const [modalReadonly, setModalReadonly] = useState(false)
  const [uploadModal, setUploadModal] = useState(false)
  const [uploadModalHeader, setUploadModalHeader] = useState('')
  const [uploadedFilesState, setUploadedFilesState] = useState(initialFiles as any)
  const [uploadingDocument, setUploadingDocument] = useState(false)
  const handleOpenModal = (modalHeader: string, readonly: boolean) => {
    setUploadModal(true)
    setUploadModalHeader(modalHeader)
    setModalReadonly(readonly)
  }

  const handleCloseUploadModal = () => {
    setUploadModal(false)
    setUploadingDocument(false)
  }

  const handleSubmitUploadFile = (values: any) => {
    const data = {
      organizationId: selectedOrganization.organization_id,
      providerId: selectedProvider.provider_id,
      email: user.email,
      ...getUploadFileAuditModalData(uploadModalHeader, values),
    }

    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      if (data[key]) {
        if (key === 'file') {
          data[key].forEach((fileItem) => formData.append(key, fileItem))
        } else {
          formData.append(key, data[key])
        }
      }
    })

    setUploadingDocument(true)
    dispatch(updateProviderFileAuditItem(token, formData))
  }

  useEffect(() => {
    if ( auditResult.success && !auditResult.loading ) {
      if (audit) {
        // Provider File Audit items
        const w9Item = audit.item ? audit.item.find((item) => item.itemType && item.itemType.includes('W-9 In Record')) : null
        const activeCertificateLicensureItem = audit.item ? audit.item.find((item) => item.itemType && item.itemType.includes('Active Certificate/Licensure')) : null
        const activeLiabilityInsuranceItem = audit.item ? audit.item.find((item) => item.itemType && item.itemType.includes('Active Liability Insurance')) : null
        const activeHomeownersInsuranceItem = audit.item ? audit.item.find((item) => item.itemType && item.itemType.includes('Active Homeowners Insurance')) : null
        const activeAutoInsuranceItem = audit.item ? audit.item.find((item) => item.itemType && item.itemType.includes('Active Auto Insurance')) : null

        setUploadedFilesState({
          ...uploadedFilesState,
          w9Id: (w9Item && w9Item.providerFileAuditItemId) || null,
          w9File: (w9Item && w9Item.attachment) || null,
          activeCertificateLicensureId: (activeCertificateLicensureItem && activeCertificateLicensureItem.providerFileAuditItemId) || null,
          activeCertificateLicensureFile: (activeCertificateLicensureItem && activeCertificateLicensureItem.attachment) || null,
          activeLiabilityInsuranceId: (activeLiabilityInsuranceItem && activeLiabilityInsuranceItem.providerFileAuditItemId) || null,
          activeLiabilityInsuranceFile: (activeLiabilityInsuranceItem && activeLiabilityInsuranceItem.attachment) || null,
          activeHomeownersInsuranceId: (activeHomeownersInsuranceItem && activeHomeownersInsuranceItem.providerFileAuditItemId) || null,
          activeHomeownersInsuranceFile: (activeHomeownersInsuranceItem && activeHomeownersInsuranceItem.attachment) || null,
          activeAutoInsuranceId: (activeAutoInsuranceItem && activeAutoInsuranceItem.providerFileAuditItemId) || null,
          activeAutoInsuranceFile: (activeAutoInsuranceItem && activeAutoInsuranceItem.attachment) || null,
        })
      } else {
        setUploadedFilesState(uploadedFilesState)
      }
    }
  }, [audit, auditResult]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [{
    dataField: 'status',
    text: 'Status',
    sort: false,
    formatter: (cellContent, row) => renderStatus(cellContent, row),
    headerStyle: { width: '10%' },
    style: { width: '10%' }
  }, {
    dataField: 'itemType',
    text: 'Item Name',
    sort: false,
    headerStyle: { width: '75%' },
    style: { width: '75%' }
  }, {
    dataField: 'modalType',
    text: 'Actions',
    sort: false,
    formatter: (cellContent, row) => renderActionButtons(row, handleOpenModal)
  }]

  return (
    <div style={{ height:height-214 }}> 
      <div className='all-audits-container'>
        <span className='all-audits-txt' onClick={onReturnToAll}>All Audits & Verifications</span> {'>'}
      </div>
      <Row style={{ height: '100%' }}>
        <Col xs={3}>
          <Sidebar 
            label='Audit'
            type='Provider Audit'
            startDate={audit && audit.auditDate}
            submitDate={null}
            approvalDate={null}
          />
        </Col>
        <Col xs={9} style={{ height: '100%', overflow: 'auto' }}>
          <Card>
            <Card.Body style={{ maxHeight: 440, height:height-216 }}>
              <Card.Title>Items Needed</Card.Title>
              <div style={{height: '100%'}}>
                <ToolkitProvider
                  keyField='providerFileAuditItemId'
                  bootstrap4
                  data={checkFileAuditItems((audit && audit.item) || [])}
                  columns={columns}
                  search
                >
                  {
                    (props: any) => (
                      <div className='audit-table' style={{width: '100%', background: 'white'}}>
                        <BootstrapTable
                          id='auditTable'
                          bordered={false}
                          condensed
                          bootstrap4
                          noDataIndication={data}
                          { ...props.baseProps }
                        />
                      </div>
                    )
                  }
                </ToolkitProvider>
              </div>
            </Card.Body>
          </Card>
          <UploadProviderFileAuditModal
            show={uploadModal}
            modalHeader={uploadModalHeader}
            readonly={modalReadonly}
            state={{
              ...uploadedFilesState,
              modalType: uploadModalHeader
            }}
            uploadingDocument={uploadingDocument}
            updateProviderFileAuditItem={updateProviderFileAuditItemResult}
            onClose={handleCloseUploadModal}
            onSubmit={handleSubmitUploadFile}
          />
        </Col>
      </Row>
    </div>
  )
}

export default Audit